import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import defaultConfig, {
  backgroundDark,
  backgroundLight,
  defaultTheme,
  textDark,
  textLight,
} from './defaultConfig';
import PropTypes from 'prop-types';
import {LayoutDirection, ThemeMode} from '../../../shared/constants/AppEnums';

const ThemeContext = createContext();
const ThemeActionsContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);
export const useThemeActionsContext = () => useContext(ThemeActionsContext);

const LOCALE_KEY = 'app_locale';
const DIR_KEY = 'app_direction';

const getInitialDirection = () => {
  try {
    const savedDir =
      typeof window !== 'undefined' && localStorage.getItem(DIR_KEY);
    if (savedDir === LayoutDirection.RTL || savedDir === LayoutDirection.LTR) {
      return savedDir;
    }
    // Fallback: infer from saved locale if present
    const savedLocaleStr =
      typeof window !== 'undefined' && localStorage.getItem(LOCALE_KEY);
    const savedLocale = savedLocaleStr
      ? JSON.parse(savedLocaleStr)
      : defaultConfig.locale;
    const base = (savedLocale?.locale || '').toLowerCase().split(/[-_]/)[0];
    const isRtl = ['ar', 'he', 'fa', 'ur', 'ps', 'ku', 'dv', 'ug'].includes(
      base,
    );
    return isRtl ? LayoutDirection.RTL : LayoutDirection.LTR;
  } catch (e) {
    return LayoutDirection.LTR;
  }
};

const ThemeContextProvider = ({children}) => {
  const [theme, setTheme] = useState({
    ...defaultTheme.theme,
    direction: getInitialDirection(),
  });
  const [themeMode, updateThemeMode] = useState(defaultConfig.themeMode);
  const [themeStyle, updateThemeStyle] = useState(defaultConfig.themeStyle);

  const updateTheme = useCallback((nextTheme) => {
    setTheme(nextTheme);
  }, []);

  // Keep palette in sync with themeMode (avoid mutating the existing object)
  useEffect(() => {
    setTheme((prev) => ({
      ...prev,
      palette: {
        ...prev.palette,
        mode: themeMode === ThemeMode.DARK ? ThemeMode.DARK : ThemeMode.LIGHT,
        background:
          themeMode === ThemeMode.DARK ? backgroundDark : backgroundLight,
        text: themeMode === ThemeMode.DARK ? textDark : textLight,
      },
    }));
  }, [themeMode]);

  // Reflect direction on <html> and persist it
  useEffect(() => {
    const dir = theme.direction === LayoutDirection.RTL ? 'rtl' : 'ltr';
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', dir);
    }
    try {
      localStorage.setItem(DIR_KEY, theme.direction);
    } catch (e) {
      // ignore storage errors
    }
  }, [theme.direction]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeStyle,
        themeMode,
      }}
    >
      <ThemeActionsContext.Provider
        value={{
          updateTheme,
          updateThemeStyle,
          updateThemeMode,
        }}
      >
        {children}
      </ThemeActionsContext.Provider>
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;

ThemeContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
